import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { Presence, type PresenceState } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import { Avatar } from '../ui/Avatar.tsx';
import type { ChatPresence, ChatStatus } from './types.ts';

/**
 * Maps the SDK's four-state `Presence` (`ChatClient.presence`'s own doc) to
 * `@wtfalch/design`'s three-state `PresenceState`. `null` for `'offline'`:
 * `Presence`'s own docblock gives on/busy/away a colour each and treats
 * away as an absence already, with no fourth state for a caller to draw --
 * so offline draws no dot at all, the same way a company with presence
 * turned off (every one of them today, per `ChatClient.presence`'s own
 * finding) already reads as everyone offline rather than everyone in some
 * invented fourth colour.
 */
export function presenceState(presence: ChatPresence): PresenceState | null {
  if (presence === 'online') return 'on';
  if (presence === 'busy') return 'busy';
  if (presence === 'away') return 'away';
  return null;
}

/** Re-renders on every `onPresenceChanged` -- `client.presence(userId)` is a
 *  live read off matrix-js-sdk's own cache (its own doc: "never kept up to
 *  date by this call itself"), so there is nothing to store here, only a
 *  reason to ask again. */
function usePresenceTick(client: ChatClient): void {
  const [, setTick] = useState(0);
  useEffect(() => client.onPresenceChanged(() => setTick((t) => t + 1)), [client]);
}

/** An `Avatar` with a presence dot at its corner, for wherever a person's
 *  disc is drawn beside their id -- a room row, a DM's head, a member row.
 *  Subscribes for itself rather than asking every caller to, so a caller
 *  reads no differently than plain `Avatar`. */
export function AvatarWithPresence({
  client,
  userId,
  name,
  seed,
  size,
}: {
  client: ChatClient;
  userId: string;
  name?: string | null;
  seed: string;
  size?: 'sm';
}) {
  usePresenceTick(client);
  const state = presenceState(client.presence(userId));
  return (
    <span className="relative inline-flex flex-none chat-avatar-presence">
      <Avatar name={name} seed={seed} size={size} />
      {state && <Presence state={state} size="sm" className="chat-presence-dot" />}
    </span>
  );
}

/** A direct room's other side -- the one person, beyond the signed-in
 *  account, `roomMembers` lists for it -- for wherever a DM's own presence
 *  needs a `userId` and `ChatRoomSummary` (which names no member) does not
 *  carry one. `null` for a channel, or a DM this client has not learned the
 *  other side's membership for yet. */
export function directPeerId(client: ChatClient, room: ChatRoomSummary): string | null {
  if (!room.isDirect) return null;
  const me = client.userId();
  return client.roomMembers(room.id).find((member) => member.userId !== me)?.userId ?? null;
}

const STATUS_LABELS: Record<ChatStatus['kind'], string | null> = {
  here: null,
  busy: 'Busy',
  meeting: 'In a meeting',
  vacation: 'On vacation',
  sick: 'Out sick',
};

/** What to draw for a status: `null` for none, `here` (the default, which
 *  needs no saying) or one whose `until` has passed -- `docs/status.md`
 *  gives every status an optional end and nothing clears it on the server. */
export function statusLabel(status: ChatStatus | null, now = Date.now()): string | null {
  if (!status) return null;
  if (status.until && new Date(status.until).getTime() <= now) return null;
  return STATUS_LABELS[status.kind];
}

/** `client.status(userId)`, re-read when it lands. The first call starts the
 *  fetch and reads `null`; `onStatusChanged` is what brings the answer. */
export function useStatus(client: ChatClient, userId: string | null): ChatStatus | null {
  const [, setTick] = useState(0);
  useEffect(
    () =>
      client.onStatusChanged((changed) => userId && changed.has(userId) && setTick((t) => t + 1)),
    [client, userId],
  );
  return userId ? client.status(userId) : null;
}

/** The status label beside a name or presence dot, drawn alongside it
 *  (status and presence both show -- one is not a stand-in for the other).
 *  Draws nothing for no status. */
export function StatusLabel({ client, userId }: { client: ChatClient; userId: string | null }) {
  const label = statusLabel(useStatus(client, userId));
  if (!label) return null;
  return <span className="text-xs text-muted flex-none chat-status-label">{label}</span>;
}

/** A DM's other side's status, above the composer, before the first
 *  keystroke (`docs/status.md`: "Not a tooltip"). Their message is the
 *  point; the label only says what kind of absence it is. */
export function StatusBanner({ client, userId }: { client: ChatClient; userId: string | null }) {
  const status = useStatus(client, userId);
  const label = statusLabel(status);
  if (!status || !label) return null;
  return (
    <div role="status" className="flex flex-col flex-none px-5 py-2 text-sm chat-status-banner">
      <span className="font-strong">{label}</span>
      {status.message && <span>{status.message}</span>}
    </div>
  );
}
